/**
 * One-call access to the device's location, on native and web alike.
 *
 * On the Capacitor app we use @capacitor/geolocation (which prompts for the OS
 * permission and reads GPS); on the web we use the browser's Geolocation API.
 * The Capacitor plugin is dynamically imported so the web bundle never pulls it
 * in, and everything degrades to a rejected promise the caller can show as
 * "couldn't get your location — search or drop a pin instead".
 */

import { isNativePlatform } from './storage.js';

// maximumAge 0: never hand back a cached fix when someone is pinning their
// front door. A 10s-old position can be hundreds of metres out if they walked
// or the phone last fixed on a cell tower.
const WEB_OPTS = { enableHighAccuracy: true, timeout: 15_000, maximumAge: 0 };

/**
 * Ask for location permission without reading a position.
 *
 * Called once at app start on the native apps so the OS prompt appears with the
 * rest of the first-run experience, rather than mid-checkout when someone taps
 * "use my location". Safe to call repeatedly: once answered, the OS doesn't
 * prompt again. A refusal is not an error here — the map still works by tapping.
 */
export async function primeLocationPermission() {
  if (!isNativePlatform()) return 'unsupported';
  try {
    const { Geolocation } = await import('@capacitor/geolocation');
    const current = await Geolocation.checkPermissions();
    if (current.location === 'granted' || current.coarseLocation === 'granted') return 'granted';
    const asked = await Geolocation.requestPermissions();
    return asked.location ?? 'denied';
  } catch {
    return 'unsupported';
  }
}

/** Resolve to { latitude, longitude, accuracy } or reject with an Error. */
export async function getCurrentLocation() {
  if (isNativePlatform()) {
    const { Geolocation } = await import('@capacitor/geolocation');
    try {
      // Ask first; on Android the plugin throws if permission is denied.
      const perm = await Geolocation.checkPermissions();
      if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') {
        await Geolocation.requestPermissions();
      }
    } catch {
      // checkPermissions can be unsupported on some platforms — fall through.
    }
    // maximumAge 0 for the same reason as the web options above: a stale fix is
    // what makes a pin land on the wrong street.
    const pos = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 15_000,
      maximumAge: 0,
    });
    return { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy };
  }

  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    throw new Error('Location is not available on this device.');
  }
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
      (err) => reject(new Error(err?.message || 'Could not read your location.')),
      WEB_OPTS,
    );
  });
}

/**
 * Watch the device position, invoking `onUpdate({latitude,longitude,accuracy})`
 * as it changes. Returns a function that stops watching. Used by the driver app
 * to stream its location while on duty.
 */
export async function watchLocation(onUpdate, onError) {
  if (isNativePlatform()) {
    const { Geolocation } = await import('@capacitor/geolocation');
    try {
      await Geolocation.requestPermissions();
    } catch {
      /* fall through — watch will error if truly denied */
    }
    const id = await Geolocation.watchPosition(
      { enableHighAccuracy: true, timeout: 20_000 },
      (pos, err) => {
        if (err) return onError?.(new Error(err.message || 'Location error'));
        if (pos) onUpdate({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy });
      },
    );
    return () => Geolocation.clearWatch({ id }).catch(() => {});
  }

  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    onError?.(new Error('Location is not available on this device.'));
    return () => {};
  }
  const id = navigator.geolocation.watchPosition(
    (pos) => onUpdate({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }),
    (err) => onError?.(new Error(err?.message || 'Could not read your location.')),
    WEB_OPTS,
  );
  return () => navigator.geolocation.clearWatch(id);
}

/** Kanpur city centre — the map's default view before a pin exists. */
export const DEFAULT_CENTER = { latitude: 26.4499, longitude: 80.3319 };
